import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ButtonStyleAlignmentEnum } from '../../../../../Common/PageBuilder/enums/button-style.enum';
import { ButtonStyleFieldModel } from '../../../../../Common/PageBuilder/models/button-style-field.model';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { SaveStylesDialogComponent } from '../../../../Dialogs/components/SaveStylesDialog/save-styles-dialog.component';
import { SaveTwigTemplateDialogComponent } from '../../../../Dialogs/components/SaveTwigTemplateDialog/save-twig-template-dialog.component';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { CheckboxTypesEnum } from '../../../../Forms/enums/checkbox-types.enum';
import { RadioTypesEnum } from '../../../../Forms/enums/radio-types.enum';
import { DropdownOptionModel } from '../../../../Forms/models/dropdown-option.model';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';
import { ManageStylesDialogComponent } from '../../../../Styles/components/ManageStylesDialog/manage-styles-dialog.component';
import { Style } from '../../../../Styles/models/style.model';
import { Template } from '../../../../Styles/models/template.model';
import { StylesService } from '../../../../Styles/services/styles.service';
import { StyleStateEnum } from '../../../enums/style-state.enum';
import { ButtonStyleModel } from '../../../models/button-style.model';
import { DropdownFieldModel } from '../../../models/dropdown-field.model';
import { textStyleFontDropdownOptions } from '../../../models/text-style-font-dropdown-options.model';
import { textStyleFontSizeDropdownOptions } from '../../../models/text-style-font-size-dropdown-options.model';
import { AbstractPageBuilderService } from '../../../services/abstract-page-builder.service';
import { AbstractBaseStyleFieldComponent } from '../../BaseStyleField/abstract-base-style-field.component';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { DialogIoModel } from '../../../../../Core/Dialogs/models/dialog-io.model';
import { BlockDisplayTypeEnum } from '../../../enums/block-display-type.enum';
import { AdvancedColorPickerFieldModel, buttonBackgroundColorConfig, buttonColorConfig, fontColorConfig } from '@Common/PageBuilder/models/advanced-color-picker-field.model';


@Component({
    selector: 'agpr-button-style-field',
    templateUrl: './button-style-field.component.html',
    styleUrls: ['./button-style-field.component.scss'],
    standalone: false
})
export class ButtonStyleFieldComponent extends AbstractBaseStyleFieldComponent implements OnInit, OnDestroy, OnChanges {
    @Input() declare value: ButtonStyleModel;
    @Input() declare field: ButtonStyleFieldModel;

    @Output() changes: EventEmitter<ButtonStyleModel> = new EventEmitter<ButtonStyleModel>();

    public fontColorPickerField: AdvancedColorPickerFieldModel;
    public fontFamilyField: DropdownFieldModel;
    public fontSizeField: DropdownFieldModel;
    public styleStateField: DropdownFieldModel;
    public buttonColorPickerField: AdvancedColorPickerFieldModel;
    public buttonBackgroundColorPickerField: AdvancedColorPickerFieldModel;

    public buttonAlignmentEnum = ButtonStyleAlignmentEnum;
    public checkboxTypesEnum = CheckboxTypesEnum;
    public radioTypesEnum = RadioTypesEnum;
    public messageSeverityEnum = messageSeverityEnum;

    readonly buttonTypes = ButtonTypesEnum;

    buttonShapeInputMax: number = 28;
    buttonShapeInputStep: number = 2;

    protected defaultStyleStateOptions: DropdownOptionModel[] = [
        { label: 'PageBuilder.formField.styles.defaultAgilityStyle', value: StyleStateEnum.DEFAULT },
        { label: 'PageBuilder.formField.styles.custom', value: StyleStateEnum.CUSTOM }
    ];
    protected subscriptions: Subscription[] = [];
    protected styles: Style[];

    constructor(
        protected coreDialogService: CoreDialogService,
        protected stylesService: StylesService,
        protected abstractPageBuilderService: AbstractPageBuilderService
    ) {
        super(coreDialogService);
        this.subscriptions.push(
            this.stylesService.all$.pipe(
                filter(x => !!x)
            ).subscribe(styles => {
                this.styles = styles;

                this.updateStyleOptions();
                this.updateStyleFromSavedStyle();
            })
        );
    }

    ngOnInit() {
        if (!this.value) {
            this.value = new ButtonStyleModel();
        }

        if (this.block?.displayElement) {
            this.field.allowExpertMode = this.block?.displayElement?.displayType !== BlockDisplayTypeEnum.ONLINE;
        }

        if (this.field?.defaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.defaultStyleStateOptions;
        }

        this.styleStateField = new DropdownFieldModel(
            null,
            'displayStyleState',
            'PageBuilder.styles.contentBlockStyle',
            '',
            this.defaultStyleStateOptions
        );
        this.updateStyleOptions();


        this.fontColorPickerField = AdvancedColorPickerFieldModel.create(fontColorConfig(true));

        this.fontFamilyField = new DropdownFieldModel(
            null,
            'fontFamily',
            '',
            '',
            textStyleFontDropdownOptions,
            '',
            'TextStyle.fontFamily.label'
        );

        this.fontSizeField = new DropdownFieldModel(
            null,
            'fontSize',
            '',
            '',
            textStyleFontSizeDropdownOptions.slice(0, -2),
            '',
            'TextStyle.fontSize.label'
        );



        this.buttonColorPickerField = AdvancedColorPickerFieldModel.create(buttonColorConfig(true));


        this.buttonBackgroundColorPickerField = AdvancedColorPickerFieldModel.create(buttonBackgroundColorConfig(true));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('value')) {
            if (!this.value) {
                this.value = new ButtonStyleModel();
            }

            if (!this.value.fontSize) {
                this.value.fontSize = this.field.defaults.fontSize;
            }
            if (!this.value.fontColor) {
                this.value.fontColor = this.field.defaults.fontColor;
            }
            if (this.value.bold === undefined) {
                this.value.bold = this.field.defaults.bold;
            }
            if (!this.value.buttonColor) {
                this.value.buttonColor = this.field.defaults.buttonColor;
            }
            if (!this.value.buttonBackgroundColor) {
                this.value.buttonBackgroundColor = this.field.defaults.buttonBackgroundColor;
            }
            if (!(this.value.buttonShape || this.value.buttonShape === 0)) {
                this.value.buttonShape = this.field.defaults.buttonShape;
            }
        }
    }


    public saveStyle(): void {
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;

        const io: DialogIoModel = {
            inputs: {
                type: this.field.styleType,
                inputStyle: this.styleModel
            },
            outputs: {
                dialogClose: (response) => {
                    if (response) {
                        this.setStyle(response, 'displayStyleState', true);
                    }
                }
            }
        };

        this.coreDialogService.open(SaveStylesDialogComponent, io);
    }

    /**
     * Opens the save-twig-template-dialog after loading the data needed for it
     */
    public saveExpertTemplate(): void {
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;
        this.styleModel.template = this.value.template;
        this.saveTwigTemplateDialogModel.inputs.defaultTemplate = this.abstractPageBuilderService.getDefaultTemplateByBlockType(
            this.abstractPageBuilderService.getBlockTypeEnumFromStyleTypeEnum(this.field.styleType),
            StyleStateEnum.DEFAULT
        );
        // Set the default template if there's not a template on this style already
        if (!this.styleModel.template) {
            this.setTemplate = true;
            this.styleModel.template = new Template();
            this.styleModel.template.template = this.saveTwigTemplateDialogModel.inputs.defaultTemplate;
        }

        this.saveTwigTemplateDialogModel.inputs.type = this.field.styleType;
        this.saveTwigTemplateDialogModel.inputs.inputStyle = this.styleModel;
        const dialogKey = this.coreDialogService.open(SaveTwigTemplateDialogComponent, this.saveTwigTemplateDialogModel);
    }

    public manageStylesButtonClicked(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageStylesDialogComponent, io);
    }

    protected updateStyleFromSavedStyle() {
        if (!this.value || !this.styles || !Number(this.value.styleState)) {
            return;
        }
        const savedStyle: Style = this.styles.find(x => x.id === this.value.styleState as number);
        if (!savedStyle) {
            // the list doesn't contain the id we're looking for, can't load it
            return;
        }
        const buttonStyleModel = new ButtonStyleModel(savedStyle.style);
        buttonStyleModel.styleState = this.value.styleState;
        buttonStyleModel.displayStyleState = this.value.displayStyleState;
        buttonStyleModel.template = savedStyle.template;

        this.value = buttonStyleModel;
    }

    protected updateStyleOptions(): void {
        if (!this.styles || !this.value || !this.value.styleState) {
            return;
        }

        const styleDropdownOptions = this.styles
            .filter(style => style.type === this.field.styleType)
            .map(
                style => {
                    return {
                        label: style.name,
                        value: style.id
                    };
                }
            );

        this.styleStateField.options = [
            ...this.defaultStyleStateOptions,
            ...styleDropdownOptions
        ];
    }

    /**
     * Calculates the current Max and Step used by the button shape slider, and then sets the
     * appropriate buttonShape value based on the slider
     */
    setButtonShape(): void {
        const oldButtonSliderValue = this.value.buttonShape / this.buttonShapeInputStep;
        this.buttonShapeInputMax = +this.value.fontSize.split('px')[0] * 2;
        this.buttonShapeInputStep = this.buttonShapeInputMax / 10;
        const newButtonShape = Math.min(oldButtonSliderValue * this.buttonShapeInputStep, this.buttonShapeInputMax);
        this.setStyle(newButtonShape, 'buttonShape');
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
